import React from 'react';

import { Button, Flexbox, Text } from '@schoolinks/ui-components';

import cn from 'classnames';
import { isString } from 'lodash';
import { bool, node, oneOfType, shape, string } from 'prop-types';

import Speaker from 'modules/ElementaryCareers/components/Speaker';

import s from './EmojiEmptyState.css';

/**
 * Empty state that renders an emoji and text
 *
 * @param {string} buttonProps - can pass any props available to Button as an object
 * @param {string} className - used for additional container styles
 * @param {bool} compact - if true, will use compact text styles
 * @param {Object} emojiProps - name and label of emoji
 * @param {string | node} mainText - text to render under emoji - should typically be a string but can pass a node if text requires a nested link or something similar
 * @param {string | node} secondaryText - smaller subtext with the same implementation guidelines as described above in mainText
 */
const EmojiEmptyState = ({
  buttonProps,
  className,
  compact,
  emojiProps,
  mainText,
  secondaryText,
}) => (
  <Flexbox
    alignItems="center"
    className={cn(s.root, className)}
    flexDirection="column"
    justifyContent="center"
  >
    <Text
      aria-label={emojiProps.label}
      mb={2}
      role="img"
      variant={compact ? 'h2' : 'h1'}
    >
      {emojiProps.emoji}
    </Text>
    {isString(mainText) ? (
      <Text
        color="grayDark"
        component="h2"
        mb={compact ? 1.5 : 2}
        variant={compact ? 'titleSmBold' : 'h5'}
      >
        <Speaker dataTtsId={mainText} smallIcon>
          {mainText}
        </Speaker>
      </Text>
    ) : (
      mainText
    )}
    {isString(secondaryText) ? (
      <Text
        align="center"
        color="grayDark"
        variant={compact ? 'bodySm' : 'lead'}
      >
        <Speaker dataTtsId={secondaryText} smallIcon>
          {secondaryText}
        </Speaker>
      </Text>
    ) : (
      secondaryText
    )}
    {buttonProps && (
      <Button
        className={cn(
          s.actionButton,
          { [s.compact]: compact },
          buttonProps?.className,
        )}
        {...buttonProps}
      />
    )}
  </Flexbox>
);

EmojiEmptyState.propTypes = {
  buttonProps: shape({}),
  className: string,
  compact: bool,
  emojiProps: shape({
    emoji: string,
    label: string,
  }).isRequired,
  mainText: oneOfType([string, node]).isRequired,
  secondaryText: oneOfType([string, node]),
};

EmojiEmptyState.defaultProps = {
  buttonProps: undefined,
  className: undefined,
  compact: false,
  secondaryText: undefined,
};

export default EmojiEmptyState;
